import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map, tap } from 'rxjs';

import {
  FormFieldComponent,
  FormFieldErrorComponent,
} from '@aw/shared/components/form-field';
import { IconModule } from '@aw/shared/components/icon';
import { InputComponent } from '@aw/shared/components/input';
import { strictEmailValidator } from '@aw/shared/validators';

import { ButtonComponent } from '@aw/prypco/components/button';
import { GtmService } from '@aw/prypco/services/script-manager';

import { OryAuthFacade } from '../../+state/ory-auth.facade';
import { OryMessageCode } from '../../constants/ory.constant';
import {
  OryAfterAuthLoginRedirectTo,
  OryAuthPages,
} from '../../constants/pages.constant';

@Component({
  selector: 'aw-ory-auth-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldErrorComponent,
    InputComponent,
    ButtonComponent,
    IconModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private gtmService = inject(GtmService);
  private oryAuthFacade = inject(OryAuthFacade);

  readonly registerRoute = ['/', OryAuthPages.ROOT, OryAuthPages.REGISTER];
  readonly errorCodes = OryMessageCode;

  // Email provider suggestions
  readonly emailProviders = ['@gmail.com', '@outlook.com', '@yahoo.com'];
  readonly emailValue = signal('');
  readonly showEmailProviders = computed(() => {
    const value = this.emailValue();
    return value.endsWith('@') && value.lastIndexOf('@') === value.length - 1;
  });

  loginForm = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, strictEmailValidator()],
    }),
  });

  // Reactive state from store
  readonly flowId$ = this.oryAuthFacade.loginFlowId$;
  readonly csrfToken$ = this.oryAuthFacade.loginCsrfToken$;

  readonly isSubmitting$ = this.oryAuthFacade.isSubmitting$;
  readonly submitError$ = this.oryAuthFacade.submitError$;
  readonly session$ = this.oryAuthFacade.session$;

  readonly isLoginFlowLoading$ = this.oryAuthFacade.loginFlowLoading$;
  readonly loginFlowError$ = this.oryAuthFacade.loginFlowError$;

  readonly error$ = combineLatest([
    this.loginFlowError$,
    this.submitError$,
  ]).pipe(map(([flowError, submitError]) => flowError || submitError));

  readonly flowIdInit$ = this.route.queryParamMap.pipe(
    map((qpm) => ({ flowId: qpm.get('flow'), email: qpm.get('email') })),
    tap(({ flowId, email }) => {
      if (flowId) {
        this.oryAuthFacade.loadLoginFlow(flowId);
      } else {
        const returnTo =
          this.route.snapshot.queryParamMap.get('returnTo') ||
          OryAfterAuthLoginRedirectTo;

        this.oryAuthFacade.startLoginFlow(returnTo);
      }

      if (email) {
        this.loginForm.patchValue({
          email,
        });
      }
    }),
  );

  protected isSubmitting = toSignal(this.isSubmitting$, {
    initialValue: false,
  });
  protected submitError = toSignal(this.submitError$, {
    initialValue: null,
  });
  protected error = toSignal(this.error$);

  protected hasSession = toSignal(
    this.session$.pipe(map((session) => !!session)),
    { initialValue: false },
  );

  ngOnInit(): void {
    // Clear flow state and errors when navigating to login
    this.oryAuthFacade.clearFlowState();
    this.oryAuthFacade.clearErrors();
    this.flowIdInit$.subscribe();

    // Track email input changes
    this.loginForm.get('email')?.valueChanges.subscribe((value) => {
      this.emailValue.set(value || '');
      // Clear errors when user starts typing
      this.oryAuthFacade.clearErrors();
    });
  }

  submitForm(): void {
    if (this.loginForm.valid && !this.isSubmitting() && !this.hasSession()) {
      this.oryAuthFacade.submitLogin(this.loginForm.getRawValue());
    }
  }

  selectEmailProvider(provider: string): void {
    const currentValue = this.loginForm.get('email')?.value || '';
    const newValue = currentValue.slice(0, -1) + provider;
    this.loginForm.patchValue({ email: newValue });
    this.emailValue.set(newValue);
  }

  goBack(): void {
    this.location.back();
  }

  onSignUp(buttonName): void {
    this.gtmServicePush(buttonName);
  }

  gtmServicePush(buttonName: string) {
    const utmParams = this.parseUTMJson();
    this.gtmService.pushEvent({
      event: 'signup_started',
      utm_source: utmParams['utm_source'] || '',
      utm_medium: utmParams['utm_medium'] || '',
      utm_campaign: utmParams['utm_campaign'] || '',
      utm_term: utmParams['utm_term'] || '',
      utm_content: utmParams['utm_content'] || '',
      click_text: buttonName,
      cta_location: 'login_page',
      timestamp: new Date().toISOString(),
    });
  }

  private parseUTMJson(): { [key: string]: string } {
    const utmRaw = localStorage.getItem('utm') || '{}';
    try {
      const parsed = JSON.parse(utmRaw);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch (e) {
      console.error('Failed to parse UTM JSON from localStorage:', e);
      return {};
    }
  }
}
